import { useApiMutation } from "@/hooks/useApiMutation";
import { getApiErrorCode } from "@/lib/api-error";
import {
  CompanyIdentifierLookupState,
  CompanyIdentifierShortcutContext,
  companyIdentifierToOpen,
} from "@/lib/company-identifier-shortcut";
import {
  CompanyIdentifier,
  CompanyIdentifierErrorCode,
  CompanyIdentifierLookupDTO,
} from "@shaarp/common";
import { useEffect, useRef, useState } from "react";

/** The API reads the message differently from the composer: it is not one. */
const NOT_A_COMPANY_IDENTIFIER: CompanyIdentifierErrorCode =
  "NOT_A_COMPANY_IDENTIFIER";

/**
 * How long the field must hold still before an identifier typed in it opens
 * its card on its own. A lookup reaches the registry and may create a company:
 * it is not spent on a text the user is still writing.
 */
export const COMPANY_IDENTIFIER_CARD_DELAY_MS = 500;

/**
 * The same wait for a SIREN, which is the first nine digits of every SIRET:
 * someone typing a SIRET by hand pauses inside it, and the card must not open
 * on the head office they were passing through.
 */
export const COMPANY_IDENTIFIER_CARD_SIREN_DELAY_MS = 1_500;

/** What tells two identifiers apart, and so one card from the next. */
const identifierKeyOf = (identifier: CompanyIdentifier): string =>
  `${identifier.type}:${identifier.value}`;

/** The card on screen: the identifier it shows, and the text it answers. */
export type OpenCompanyIdentifierCard = {
  readonly query: string;
  readonly identifier: CompanyIdentifier;
  /** `identifierKeyOf(identifier)`, the card's identity across text edits. */
  readonly key: string;
  /**
   * The card came up on its own, the text having settled — nobody asked for
   * anything. A refusal then closes it silently instead of sending the
   * message the user never submitted.
   */
  readonly isAutomatic: boolean;
};

export type CompanyIdentifierCard = {
  readonly current: OpenCompanyIdentifierCard | null;
  readonly lookup: CompanyIdentifierLookupState;
  /**
   * Takes the message instead of sending it, when it is one of the four
   * identifiers typed alone — for an Enter that beats the delay, or after the
   * card was dismissed. `true` when the card took it, which it also is while a
   * card is already on screen for it, where a second send would only repeat
   * the lookup.
   *
   * A name never takes a send: it is the one reading of a message that can be
   * wrong, and pressing Enter has to send what was typed.
   */
  readonly open: () => boolean;
  readonly close: () => void;
  readonly retry: () => void;
};

/** What each reading of the message is looked up with. */
type CompanyIdentifierLookupBody = { query: string } | { name: string };

/**
 * Where each reading of the message is resolved. A name goes to the route that
 * only ever reads the database, which is what lets it be asked while the
 * message is still being typed.
 */
const lookupPathFor = (body: CompanyIdentifierLookupBody): string =>
  "name" in body ? "/companies/identify-by-name" : "/companies/identify";

/** The body resolving this card's identifier, on the route that takes it. */
const lookupBodyFor = (
  card: OpenCompanyIdentifierCard,
): CompanyIdentifierLookupBody =>
  card.identifier.type === "name"
    ? { name: card.identifier.value }
    : { query: card.query.trim() };

/**
 * The card an identifier typed alone in the chat opens above the composer, in
 * place of sending the message: no turn, no conversation, and so no title for
 * a model to write.
 *
 * `message` and `context` are what the composer holds now. The card comes up
 * on its own once the text has settled on an identifier, and follows that
 * identifier rather than the exact text: a trailing space, or a path added to
 * a website, keeps the same company on screen and spends no second lookup.
 * Anything that makes the message a question for the chat again — another
 * identifier, a sentence, a file staged beside it — closes it, as does
 * `close()`, after which only a deliberate `open()` brings it back for that
 * same text.
 *
 * `onSendToChat` sends the message the usual way: where a card the user
 * *submitted* goes when the API does not read it as an identifier after all,
 * the two copies of the rule having drifted.
 */
export function useCompanyIdentifierCard(
  message: string,
  context: CompanyIdentifierShortcutContext,
  onSendToChat: () => void,
): CompanyIdentifierCard {
  const [current, setCurrent] = useState<OpenCompanyIdentifierCard | null>(
    null,
  );
  // The identifier a card was closed on. While the field still designates that
  // company, the card does not come back on its own: Escape would otherwise
  // reopen it half a second later.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const { mutate, reset, isError, isSuccess, data } = useApiMutation<
    CompanyIdentifierLookupBody,
    CompanyIdentifierLookupDTO
  >({ path: lookupPathFor });

  // A refusal lands renders after the lookup started: it sends with what the
  // composer holds then, which is the message the card was submitted on —
  // anything else would have closed the card and detached the lookup.
  const sendToChatRef = useRef(onSendToChat);
  useEffect(() => {
    sendToChatRef.current = onSendToChat;
  });

  const identifier = companyIdentifierToOpen(message, context);
  const identifierKey = identifier ? identifierKeyOf(identifier) : null;

  const isStillTaken = current !== null && current.key === identifierKey;

  useEffect(() => {
    if (current && !isStillTaken) {
      setCurrent(null);
      reset();
    }
  }, [current, isStillTaken, reset]);

  // A text that stops designating a company clears the refusal with it: the
  // same identifier typed again after that is a fresh request.
  useEffect(() => {
    if (identifierKey === null && dismissedKey !== null) setDismissedKey(null);
  }, [identifierKey, dismissedKey]);

  const lookUp = (card: OpenCompanyIdentifierCard): void => {
    mutate(lookupBodyFor(card), {
      onError: (error) => {
        if (getApiErrorCode(error) !== NOT_A_COMPANY_IDENTIFIER) return;
        setCurrent(null);
        reset();
        // Nobody submitted this text: the card simply gives up on it. Sending
        // it would start a turn the user never asked for, mid-typing.
        if (card.isAutomatic) {
          setDismissedKey(card.key);
          return;
        }
        sendToChatRef.current();
      },
    });
  };

  const openCard = (
    query: string,
    identifierToShow: CompanyIdentifier,
    isAutomatic: boolean,
  ): void => {
    const card: OpenCompanyIdentifierCard = {
      query,
      identifier: identifierToShow,
      key: identifierKeyOf(identifierToShow),
      isAutomatic,
    };

    setCurrent(card);
    setDismissedKey(null);
    lookUp(card);
  };

  // Read by the delayed opening below, so that it always fires on the text the
  // field holds when it does, not on the one that armed it.
  const autoOpenRef = useRef<() => void>(() => {});
  useEffect(() => {
    autoOpenRef.current = () => {
      if (identifier) openCard(message, identifier, true);
    };
  });

  const isAwaitingCard =
    identifierKey !== null &&
    identifierKey !== dismissedKey &&
    current === null;
  const cardDelayMs =
    identifier?.type === "siren"
      ? COMPANY_IDENTIFIER_CARD_SIREN_DELAY_MS
      : COMPANY_IDENTIFIER_CARD_DELAY_MS;

  useEffect(() => {
    if (!isAwaitingCard) return;

    const timer = setTimeout(() => autoOpenRef.current(), cardDelayMs);

    return () => clearTimeout(timer);
  }, [isAwaitingCard, identifierKey, cardDelayMs]);

  const open = (): boolean => {
    // A name is a reading of the message, not a fact about it: it must never
    // hold back an Enter, whether its card is on screen or not. The message
    // goes to the chat, the card goes with the emptied field.
    if (identifier?.type === "name") return false;
    if (current) return true;
    if (!identifier) return false;

    openCard(message, identifier, false);
    return true;
  };

  const close = (): void => {
    if (current) setDismissedKey(current.key);
    setCurrent(null);
    reset();
  };

  const retry = (): void => {
    if (current) lookUp(current);
  };

  const lookup: CompanyIdentifierLookupState = isError
    ? { status: "failed" }
    : isSuccess
      ? { status: "settled", result: data }
      : { status: "loading" };

  // A name only ever appears as the company it matched. Where an identifier
  // earns a card in every state — someone typed a SIRET and is owed an answer
  // about it — a name is a guess the composer makes on its own, and a
  // "searching…" or "no such company" on half of what anyone types would be
  // noise about a question they never asked.
  const isUnmatchedName =
    current?.identifier.type === "name" &&
    (lookup.status !== "settled" || lookup.result.status !== "found");

  useEffect(() => {
    if (!current || !isUnmatchedName || lookup.status === "loading") return;
    setDismissedKey(current.key);
    setCurrent(null);
    reset();
  }, [current, isUnmatchedName, lookup.status, reset]);

  return {
    current: isUnmatchedName ? null : current,
    lookup,
    open,
    close,
    retry,
  };
}
